import React, { useEffect, useRef, useState } from "react";
import { GameFiles, useGameFiles, useMyEntitlements } from "@seabrookstudios/pitch2table";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { getImportUploadSkipMessage, shouldSkipImportUpload } from "./importUploadUtils";
import { hasExistingImportedGameMedia, useUploadGameAsset } from "../files/uploadGameAsset";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {File} props.file
 * @param {number} props.fileIndex
 * @param {boolean} props.hasLargeFileUploads
 * @param {() => void} props.onStart
 * @param {() => void} props.onComplete
 * @param {() => void} props.onSkip
 * @param {() => void} props.onError
 */
const UploadMediaFile = ({ gameId, file, fileIndex, hasLargeFileUploads, onStart, onComplete, onSkip, onError }) => {
  const { refetch } = useGameFiles(gameId);
  const onStartRef = useRef(onStart);
  const onCompleteRef = useRef(onComplete);
  const onSkipRef = useRef(onSkip);
  const onErrorRef = useRef(onError);
  const refetchRef = useRef(refetch);
  onStartRef.current = onStart;
  onCompleteRef.current = onComplete;
  onSkipRef.current = onSkip;
  onErrorRef.current = onError;
  refetchRef.current = refetch;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const onFileUploadSuccess = async () => {
    enqueueSnackbar(`${file.name} uploaded`, { variant: "success" });
    await queryClient.invalidateQueries({ queryKey: GameFiles(gameId) });
    onCompleteRef.current();
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading asset", { variant: "error" });
    onErrorRef.current();
  };

  const uploadAsset = useUploadGameAsset(gameId, onFileUploadSuccess, onFileUploadFailure);
  const uploadAssetRef = useRef(uploadAsset);
  uploadAssetRef.current = uploadAsset;

  useEffect(() => {
    let active = true;

    const uploadIfNeeded = async () => {
      if (shouldSkipImportUpload(file, hasLargeFileUploads)) {
        if (!active) {
          return;
        }

        enqueueSnackbar(getImportUploadSkipMessage(file), { variant: "warning" });
        onSkipRef.current();
        return;
      }

      try {
        const result = await refetchRef.current();

        if (!active) {
          return;
        }

        const files = result.data || [];

        if (hasExistingImportedGameMedia(files, file)) {
          onStartRef.current();
          onCompleteRef.current();
          return;
        }

        onStartRef.current();
        uploadAssetRef.current(
          "asset",
          file,
          {},
          () => {},
          () => {},
          () => {},
          () => {}
        );
      } catch {
        if (!active) {
          return;
        }

        enqueueSnackbar("Could not check existing game media", { variant: "error" });
        onErrorRef.current();
      }
    };

    uploadIfNeeded();

    return () => {
      active = false;
    };
  }, [enqueueSnackbar, file, fileIndex, gameId, hasLargeFileUploads]);

  return null;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {File[]} props.files
 * @param {(index: number) => void} props.onItemStart
 * @param {(index: number) => void} props.onItemComplete
 * @param {(index: number) => void} props.onItemSkip
 * @param {(index: number) => void} props.onItemError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
export const GameImportMediaUpload = ({
  gameId,
  files,
  onItemStart,
  onItemComplete,
  onItemSkip,
  onItemError,
  onComplete,
  onError,
}) => {
  const { isFetched } = useGameFiles(gameId);
  const { data: entitlements, isLoading: isEntitlementsLoading } = useMyEntitlements();
  const hasLargeFileUploads = Boolean(entitlements?.hasLargeFileUploads);
  const [fileIndex, setFileIndex] = useState(0);
  const hasCompletedBatch = useRef(false);
  const startedIndices = useRef(/** @type {Set<number>} */ (new Set()));
  const completedIndices = useRef(/** @type {Set<number>} */ (new Set()));
  const skippedIndices = useRef(/** @type {Set<number>} */ (new Set()));
  const onCompleteRef = useRef(onComplete);
  const onItemCompleteRef = useRef(onItemComplete);
  const onItemSkipRef = useRef(onItemSkip);
  onCompleteRef.current = onComplete;
  onItemCompleteRef.current = onItemComplete;
  onItemSkipRef.current = onItemSkip;
  const currentFile = files[fileIndex];

  const markItemComplete = (completedIndex) => {
    if (completedIndices.current.has(completedIndex)) {
      return;
    }

    completedIndices.current.add(completedIndex);
    onItemCompleteRef.current(completedIndex);
  };

  const finishBatch = () => {
    if (hasCompletedBatch.current) {
      return;
    }

    hasCompletedBatch.current = true;

    for (const startedIndex of startedIndices.current) {
      markItemComplete(startedIndex);
    }

    onCompleteRef.current();
  };

  const advanceFromIndex = (currentIndex) => {
    if (hasCompletedBatch.current) {
      return;
    }

    const nextIndex = currentIndex + 1;

    if (nextIndex >= files.length) {
      finishBatch();
      return;
    }

    setFileIndex(nextIndex);
  };

  useEffect(() => {
    if (fileIndex < files.length || hasCompletedBatch.current) {
      return;
    }

    finishBatch();
  }, [fileIndex, files.length]);

  if (!isFetched || isEntitlementsLoading) {
    return null;
  }

  if (!currentFile) {
    return null;
  }

  const handleItemStart = (index) => {
    if (hasCompletedBatch.current || startedIndices.current.has(index)) {
      return;
    }

    startedIndices.current.add(index);
    onItemStart(index);
  };

  const handleItemComplete = (completedIndex) => {
    markItemComplete(completedIndex);
    advanceFromIndex(completedIndex);
  };

  const handleItemSkip = (skippedIndex) => {
    if (skippedIndices.current.has(skippedIndex)) {
      return;
    }

    skippedIndices.current.add(skippedIndex);
    onItemSkipRef.current(skippedIndex);
    advanceFromIndex(skippedIndex);
  };

  return (
    <UploadMediaFile
      key={fileIndex}
      gameId={gameId}
      file={currentFile}
      fileIndex={fileIndex}
      hasLargeFileUploads={hasLargeFileUploads}
      onStart={() => {
        handleItemStart(fileIndex);
      }}
      onComplete={() => {
        handleItemComplete(fileIndex);
      }}
      onSkip={() => {
        handleItemSkip(fileIndex);
      }}
      onError={() => {
        onItemError(fileIndex);
        onError();
      }}
    />
  );
};
